/* oxlint-disable @typescript-eslint/return-await */
import { useQuery } from '@tanstack/react-query'

import { Country, Invoice, InvoiceStatus } from '@/lib/definitions/api.types'
import { GenericObject } from '@/lib/definitions/generic.types'
import { Notify } from '@/lib/misc/Notify'
import { ApiService } from '@/lib/services/ApiService'
import { UserService } from '@/lib/services/UserService'
import { StatusStyle } from '@/lib/definitions/types'
import { BillingService } from '../services/BillingService'

type InvoiceStatusLegendType = Record<
  InvoiceStatus,
  {
    label: string
    style: StatusStyle
  }
>

const invoiceStatusLegend: InvoiceStatusLegendType = {
  draft: { label: 'Draft', style: 'success' },
  open: { label: 'Open', style: 'warning' },
  overdue: { label: 'Overdue', style: 'warning' },
  cancelled: { label: 'Cancelled', style: 'neutral' },
  partially_paid: { label: 'Partially Payed', style: 'warning' },
  paid: { label: 'Paid', style: 'success' },
}

const userService = new UserService()
const apiService = new ApiService()
const billingService = new BillingService()

export interface CompanyUser {
  id: number
  name: {
    firstName: string
    lastName: string
  }
  email: string
  status: {
    label: string
    style: string
  }
  portal_login_enabled: boolean
}

interface CitySearchRequest {
  city: string
  country?: string
}

export const useLoadUsers = (params?: GenericObject) =>
  useQuery({
    queryKey: ['fetchUsers'],
    queryFn: async () => await userService.getUsers(),
    meta: {
      error: { message: 'Could not load the users' },
    },
    ...params,
  })

export const useLoadUser = (userId: string | number, params?: GenericObject) =>
  useQuery({
    queryKey: ['fetchUser', userId],
    queryFn: async () => await userService.getUser(userId),
    meta: {
      error: { message: 'Could not load this user' },
    },
    ...params,
  })

export const useLoadCompanyProfile = (
  tenant?: boolean,
  customerId?: string,
  enabled?: boolean
) =>
  useQuery({
    queryKey: ['fetchCompanyProfile', tenant, customerId],
    queryFn: async () => await userService.getCompanyProfile(tenant),
    ...(enabled !== undefined && { enabled }),
    meta: {
      error: { message: 'Could not load your company profile' },
    },
    select: (data) => {
      if (data.data.company) {
        return data.data.company
      }
      Notify.error('Could not load your company profile')
    },
  })

export const useLoadPeeringDBInfo = () =>
  useQuery({
    queryKey: ['fetchPeeringDBInfo'],
    queryFn: async () => await userService.getPeeringDBInfo(),
    select: (data) => data.data,
  })

export const useLoadCustomerPeeringDBInfo = () =>
  useQuery({
    queryKey: ['fetchCustomerPeeringDBInfo'],
    queryFn: async () => await userService.getCustomerPeeringDBInfo(),
    retry: false,
    select: (data) => data.data,
  })

export const useLoadInvoices = () =>
  useQuery({
    queryKey: ['fetchInvoices'],
    queryFn: async () => await apiService.getInvoices(),
    meta: {
      error: { message: 'Could not load the invoices' },
    },
    select: (data) =>
      data.data.map((invoice: Invoice) => ({
        order_number: invoice.order_number,
        amount: invoice.amount,
        date: invoice.date,
        due_date: invoice.due_date,
        status: invoiceStatusLegend[invoice.status],
        download_url: `${import.meta.env.VITE_PORTAL_API_HOST || ''}/invoice/download/${
          invoice.id
        }?token=${invoice.token}`,
      })),
  })

export const useLoadCountries = () =>
  useQuery({
    queryKey: ['fetchCountries'],
    queryFn: async () => await apiService.getCountries(),
    meta: {
      error: { message: 'Could not load the countries list' },
    },
    select: (data) =>
      data.data.map((country: Country) => ({
        ...country,
      })),
  })

export const useLoadCities = ({ city, country }: CitySearchRequest) =>
  useQuery({
    queryKey: ['fetchCities', city],
    queryFn: async () => await apiService.getCities(city, country),
    enabled: !!country,
    meta: {
      error: { message: 'Could not load the cities list' },
    },
    select: (data) => data.data,
  })

export const useLoadApiKeys = (params?: GenericObject) =>
  useQuery({
    queryKey: ['fetchApiKeys'],
    queryFn: async () => await userService.getApiKeys(),
    meta: {
      error: { message: 'Could not load your API keys' },
    },
    select: (data) => data.data,
    ...params,
  })

export const useLoadTwoFactorAuthStatus = () =>
  useQuery({
    queryKey: ['fetchTwoFactorAuthStatus'],
    queryFn: async () => await userService.getTwoFactorAuthStatus(),
    retry: 0,
    meta: {
      error: { message: 'Could not load your 2FA status' },
    },
    select: (data) => data.data,
  })

export const useLoadTwoFactorEnforcementStatus = () =>
  useQuery({
    queryKey: ['fetchTwoFactorEnforcementStatus'],
    queryFn: async () => await userService.getTwoFactorEnforcementStatus(),
    retry: 0,
    meta: {
      error: { message: 'Could not load your 2FA enforcement status' },
    },
    select: (data) => data.data,
  })

export const useLoadSepaMandateStatus = (params?: GenericObject) =>
  useQuery({
    queryKey: ['fetchSepaMandateStatus'],
    queryFn: async () => await billingService.getSepaMandateStatus(),
    meta: {
      error: {
        message: 'Could not load your SEPA mandate. Please try again later.',
      },
    },
    select: (data) => data.data,
    ...params,
  })
